// BETA 15.30 — Real telemetry / error reporting (PSBOT RUN)
// =============================================================================
// User direction (verbatim): "if there is ever and high end percentage usage in
// game that causes crashes or frame drops and such those code need to be save
// or where they are at and the problem is and such... i mean i think most games
// have error codes that pop up or save to github or something. our own erro
// codes with such things. that way we could be even more precice. idk if its
// vercel or supabase. but i feel like there is a way to never have to guess"
//
// What this does:
// - Catches every uncaught error (`window.onerror`) + every unhandled promise
//   rejection (`window.onunhandledrejection`) and tags with our error code
// - Receives `recordPerfEvent(code, context)` calls from in-engine detectors
//   (e.g. long-frame from autoPlaytester) and forwards to Supabase
// - All sends are async, off the critical path, debounced (max 1 send per
//   error code per 30s), and best-effort (any failure is silently swallowed)
// - Falls through to localStorage `goopzone_perf_events` if Supabase fails so
//   nothing is lost — user can extract later via DevTools console
//
// Will it cause more crashes? NO if we obey the rules above. Specifically:
// - send via setTimeout(fn, 0) so it never blocks the game frame
// - try/catch around EVERY operation including the catch handlers
// - never throw, never return a rejected promise
// - dedupe so we don't queue 1000 events for the same root cause
//
// Schema: writes to existing `bug_reports` table (no schema change needed) with
// category='perf' so they're filterable in the dashboard. Title = error code.
// Description = JSON context. If volume is high we can move to a dedicated
// `perf_events` table later.
// =============================================================================

import { supabase } from './supabase';
// BETA 16.06 — Read live-play username from sessionTracker (set on game
// startSession). Was reading localStorage which didn't actually contain
// the username field — landed every row as 'live-play:anonymous'.
import { getSessionData } from '../game/sessionTracker';

// ─── Error codes ─────────────────────────────────────────────────────
// New codes go here, prefixed `ERR_`. Keeping them as string literals so
// they're greppable in the dashboard and don't drift.
export const ERR = {
  LONG_FRAME: 'ERR_LONG_FRAME',                 // single frame > 250ms
  RUNTIME_ERROR: 'ERR_RUNTIME_ERROR',           // window.onerror
  UNHANDLED_REJECTION: 'ERR_UNHANDLED_REJECTION', // window.onunhandledrejection
  HEARTBEAT_GAP: 'ERR_HEARTBEAT_GAP',           // periodic heartbeat skipped — page froze
  MEMORY_PRESSURE: 'ERR_MEMORY_PRESSURE',       // performance.memory > 90% of jsHeapSizeLimit
  GAME_LOOP_CRASH: 'ERR_GAME_LOOP_CRASH',       // 23.53 — frame armor caught a throw in update/render
} as const;
export type ErrCode = typeof ERR[keyof typeof ERR];

// ─── 23.53 — VISIBLE CRASH BANNER + armored-loop reporting ──────────────────
// Dylan (after the far-zoom black screen): "build something that will produce
// us some error so you can see the bugs in real time." Two gaps this closes:
// (1) errors were reported silently — Dylan never KNEW one fired; now a red
// banner shows on screen for 12s. (2) the 23.52 frame armor CATCHES loop
// crashes, so they never reach window.onerror — the armor must self-report.

function showCrashBanner(msg: string): void {
  try {
    let el = document.getElementById('__crash_banner');
    if (!el) {
      el = document.createElement('div');
      el.id = '__crash_banner';
      el.style.cssText =
        'position:fixed;bottom:12px;left:50%;transform:translateX(-50%);z-index:99999;' +
        'background:#7a1212;color:#ffe9e9;font:600 13px monospace;padding:8px 14px;' +
        'border:1px solid #ff5b5b;border-radius:6px;max-width:80vw;pointer-events:none;';
      document.body.appendChild(el);
    }
    el.textContent = `⚠ ERROR CAUGHT + AUTO-REPORTED — ${msg.slice(0, 120)}`;
    el.style.display = 'block';
    setTimeout(() => { if (el) el.style.display = 'none'; }, 12000);
  } catch { /* the reporter must never crash anything itself */ }
}

/** Game position/phase snapshot for crash context (best-effort). */
function devSnapshot(): Record<string, unknown> {
  try {
    const devPos = (window as unknown as { __devPos?: () => string }).__devPos;
    const s = devPos ? devPos() : '';
    return s && s !== 'none' ? JSON.parse(s) : {};
  } catch { return {}; }
}

/** 23.53 — called from the GameCanvas frame armor when update/render throws. */
export function reportLoopCrash(err: unknown, source: string): void {
  try {
    const e = err instanceof Error ? err : new Error(String(err));
    showCrashBanner(e.message);
    const snap = devSnapshot();
    recordPerfEvent(ERR.GAME_LOOP_CRASH, {
      source,
      message: e.message.slice(0, 300),
      stack: (e.stack || '').slice(0, 1500),
      pos: snap.pos, phase: snap.phase, clearance: snap.clr, mapdev: snap.mapdev,
      url: window.location.pathname + window.location.search,
    });
  } catch { /* never throw from the reporter */ }
}

// ─── Debounce + dedupe ───────────────────────────────────────────────
// Per error code, no more than 1 send per DEBOUNCE_MS. Critical for
// long-frame events which can fire many times during a hung period.
const DEBOUNCE_MS = 30_000; // 30s
const _lastSendByCode = new Map<string, number>();

// Local fallback log — ALWAYS write here even if Supabase fires fine, so user
// can grab via DevTools `localStorage.getItem('goopzone_perf_events')` even
// from a frozen tile after recovery.
const LOCAL_KEY = 'goopzone_perf_events';
const LOCAL_MAX_ENTRIES = 200; // bound storage so we never blow localStorage quota

function appendLocal(code: ErrCode, context: Record<string, unknown>): void {
  try {
    const raw = localStorage.getItem(LOCAL_KEY);
    const arr: unknown[] = raw ? JSON.parse(raw) : [];
    arr.push({ code, t: new Date().toISOString(), ...context });
    // Keep only the most recent N entries
    const trimmed = arr.length > LOCAL_MAX_ENTRIES ? arr.slice(-LOCAL_MAX_ENTRIES) : arr;
    localStorage.setItem(LOCAL_KEY, JSON.stringify(trimmed));
  } catch { /* quota / private mode — drop */ }
}

// ─── Public API ──────────────────────────────────────────────────────

/**
 * Record a perf / error event. Best-effort, never throws. Debounced per code.
 * Always writes to localStorage immediately. Async-fires to Supabase off the
 * critical path so it can't block the frame.
 */
export function recordPerfEvent(code: ErrCode, context: Record<string, unknown> = {}): void {
  // BETA 15.35 — Async-defer the localStorage append too. Was sync and could
  // hit 50-100ms when goopzone_perf_events was full (200 entries × ~1KB). Per
  // Dylan: "the game what running completly fine yesterday. you did something
  // today" — the telemetry I shipped today was doing too much sync work.

  // Debounce Supabase send by code (still sync — it's cheap, just a Map lookup)
  const now = Date.now();
  const last = _lastSendByCode.get(code) ?? 0;
  const shouldSendSupabase = now - last >= DEBOUNCE_MS;
  if (shouldSendSupabase) _lastSendByCode.set(code, now);

  // Fire-and-forget — both localStorage append AND Supabase fetch on next macrotask
  setTimeout(() => {
    appendLocal(code, context);
    if (shouldSendSupabase) {
      void sendToSupabase(code, context).catch(() => { /* noop */ });
    }
  }, 0);
}

async function sendToSupabase(code: ErrCode, context: Record<string, unknown>): Promise<void> {
  try {
    const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
    const tile = params.get('tile') ?? 'main';
    const isBotFarm = params.get('botfarm') === 'true';
    // BETA 15.91 — Username in reporter for live play. Helps when triaging
    // crash/perf events: we now know which player hit the issue.
    const reporter = isBotFarm
      ? `bot:${tile}`
      : `live-play:${getLivePlayUsername()}`;

    // BETA 16.71 — Per Dylan: "we need to know what is working and what
    // isnt working and it is all trackable." Tag every error / perf
    // event with the same device fingerprint live_play runs carry, so
    // PSBOT can answer "are these crashes iPhone-specific?" from the
    // bug_reports table without joining anything. Lazy import to avoid
    // a circular dep with perfTelemetry being imported by core modules.
    let deviceCtx: Record<string, unknown> = {};
    try {
      const dd = await import('../game/deviceDetect');
      const w = typeof window !== 'undefined' ? window : null;
      deviceCtx = {
        deviceClass: dd.classifyDevice(),
        isTouch: dd.isTouchDevice(),
        isStandalonePWA: dd.isStandalonePWA(),
        isIPhoneSafari: dd.isIPhoneSafari(),
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 200) : '',
        viewportWidth: w?.innerWidth ?? 0,
        viewportHeight: w?.innerHeight ?? 0,
        devicePixelRatio: w?.devicePixelRatio ?? 1,
        orientation: (w && w.innerHeight > w.innerWidth) ? 'portrait' : 'landscape',
      };
    } catch { /* deviceDetect failed; ship the event without device tag */ }

    const fullContext = { ...deviceCtx, ...context };
    // Best-effort short description (truncate if huge)
    let description = '';
    try { description = JSON.stringify(fullContext).slice(0, 4000); }
    catch { description = String(fullContext).slice(0, 4000); }

    await supabase.from('bug_reports').insert({
      reporter,
      category: 'perf',
      title: code,
      description,
      screen: isBotFarm ? 'botfarm' : 'play',
    });
  } catch { /* silently swallow — local has it */ }
}

// ─── Global error capture ────────────────────────────────────────────
// Runs once at module-import time. Wraps window.onerror + onunhandledrejection
// so EVERY uncaught error becomes a perf event. Idempotent — guarded by a
// module-scope flag so re-imports don't double-install.

let _globalHandlersInstalled = false;
export function installGlobalErrorHandlers(): void {
  if (_globalHandlersInstalled) return;
  if (typeof window === 'undefined') return;
  _globalHandlersInstalled = true;

  window.addEventListener('error', (ev: ErrorEvent) => {
    try {
      showCrashBanner(ev.message);   // 23.53 — Dylan sees it fire, not just Supabase
      recordPerfEvent(ERR.RUNTIME_ERROR, {
        message: ev.message,
        src: ev.filename ?? '',
        line: ev.lineno ?? 0,
        col: ev.colno ?? 0,
        stack: (ev.error && (ev.error as { stack?: string }).stack) ? String((ev.error as { stack?: string }).stack).slice(0, 1500) : '',
        url: window.location.pathname + window.location.search,
      });
    } catch { /* never let our handler crash the page */ }
  });

  window.addEventListener('unhandledrejection', (ev: PromiseRejectionEvent) => {
    try {
      const reason = ev.reason;
      const message = reason instanceof Error ? reason.message : String(reason).slice(0, 500);
      const stack = reason instanceof Error && reason.stack ? reason.stack.slice(0, 1500) : '';
      recordPerfEvent(ERR.UNHANDLED_REJECTION, {
        message,
        stack,
        url: window.location.pathname + window.location.search,
      });
    } catch { /* noop */ }
  });

  // Heartbeat — every 5s, log the wall-clock delta. If the delta is much
  // larger than 5s AND the tab is currently visible, the page was frozen
  // (event loop blocked).
  //
  // BETA 15.42 — Track tab visibility. Earlier rev (15.30) reported every gap
  // > 8s as a freeze, but Chrome throttles backgrounded tabs' setInterval to
  // 1Hz max — every backgrounded tick produced a 60s "gap" = 268 false-positive
  // ERR_HEARTBEAT_GAP events in Supabase, all exactly 60.0s, all from
  // 'live-play' (Dylan tabbing away from the game). True freezes have variable
  // gap ms and happen while the tab is foregrounded. Skip if hidden, and reset
  // the heartbeat anchor when visibility changes so the next foreground beat
  // doesn't false-positive.
  let _lastBeat = Date.now();
  let _wasHidden = (typeof document !== 'undefined' && document.hidden) || false;
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
      // On any visibility transition, reset the anchor — the next heartbeat is
      // a fresh start, not a "freeze" gap from being backgrounded.
      _lastBeat = Date.now();
      _wasHidden = document.hidden;
    });
  }
  setInterval(() => {
    try {
      const now = Date.now();
      const delta = now - _lastBeat;
      _lastBeat = now;
      // Only count a gap as a freeze if the tab is currently visible AND was
      // visible at the previous beat. Anything else is just background throttle.
      const isVisibleNow = typeof document === 'undefined' || !document.hidden;
      const wasVisibleAtLastBeat = !_wasHidden;
      _wasHidden = !isVisibleNow;
      if (delta > 8000 && isVisibleNow && wasVisibleAtLastBeat) {
        recordPerfEvent(ERR.HEARTBEAT_GAP, {
          gapMs: delta,
          memMB: getMemMB(),
          url: window.location.pathname + window.location.search,
        });
      }
      // Memory pressure check (always — if memory is bad even when backgrounded
      // we want to know).
      const mem = getMemInfo();
      if (mem && mem.usedMB > mem.limitMB * 0.9) {
        recordPerfEvent(ERR.MEMORY_PRESSURE, {
          usedMB: Math.round(mem.usedMB),
          limitMB: Math.round(mem.limitMB),
          ratio: Math.round((mem.usedMB / mem.limitMB) * 100) / 100,
        });
      }
    } catch { /* noop */ }
  }, 5000);
}

function getMemInfo(): { usedMB: number; limitMB: number } | null {
  try {
    const perf = performance as Performance & { memory?: { usedJSHeapSize: number; jsHeapSizeLimit: number } };
    if (!perf.memory) return null;
    return {
      usedMB: perf.memory.usedJSHeapSize / 1024 / 1024,
      limitMB: perf.memory.jsHeapSizeLimit / 1024 / 1024,
    };
  } catch { return null; }
}

function getMemMB(): number {
  return Math.round(getMemInfo()?.usedMB ?? 0);
}

// ─── Auto-install on import ──────────────────────────────────────────
// Cheap to call — guarded inside. Anything that imports this file gets
// global error capture for free.
installGlobalErrorHandlers();

// ─── BETA 15.30 — Per-run auto-post to Supabase ───────────────────────
// Per Dylan (verbatim): "fuck this is annoying. can it just send all the data
// to supabase or github or vercel after runs automatically with everything.
// even if it crashes." + "csv's should be easier to deal with so you guys
// see everything"
//
// What this does: after every completed run (autoPlaytester finalizeRun
// fires this), the run's full data is posted to `bug_reports` with
// category='bot_run'. Each completed run is in the database within seconds.
// If the farm crashes mid-run, only the IN-PROGRESS run is lost — every
// previously completed run is already in Supabase.
//
// No stop button, no DevTools, no master CSV aggregation. Just query
// `bug_reports WHERE category = 'bot_run'` to see everything.
//
// Same safety guarantees as recordPerfEvent:
// - async via setTimeout(0) — never blocks the frame
// - try/catch wrapping — never throws
// - localStorage fallback — events recoverable even if Supabase is down
// NO debounce here (unlike perf events) — every run is unique data we want.

const LOCAL_RUNS_KEY = 'goopzone_bot_runs';
// BETA 15.35 — was 500 entries (= ~2.5MB JSON parse+stringify per call =
// 200-500ms blocking work). Per Dylan: "the game what running completly
// fine yesterday. you did something today to cause this." I added the
// telemetry pipeline TODAY (15.30/15.31) and didn't realize the
// localStorage backup was doing huge sync I/O on every call. 50 entries
// is plenty as a safety net (Supabase is the canonical store now).
const LOCAL_RUNS_MAX_ENTRIES = 50;
const RUN_DESCRIPTION_MAX_BYTES = 100_000; // 100KB per row — fits any realistic run

/**
 * Record a completed OR in-progress bot/play run. Best-effort, never throws.
 * Posts to Supabase + writes to localStorage backup.
 *
 * @param run    The RunLog object (any JSON-serializable shape)
 * @param title  'RUN' for completed runs (default), 'RUN_PARTIAL' for periodic
 *               in-progress snapshots (every 30s while a run is alive),
 *               'RUN_FREEZE_PROXIMATE' for snapshots taken immediately when a
 *               long-frame is detected (so we have data even if the next
 *               frame is the freeze that kills the tile).
 */
export function recordBotRunEvent(run: object, title: string = 'RUN'): void {
  // BETA 15.35 — Move BOTH the localStorage backup AND the Supabase send to
  // setTimeout(0) so the call site never blocks. Per Dylan: 30s in-progress
  // snapshots + finalizeRun + freeze-proximate were collectively spending
  // hundreds of ms per call on synchronous localStorage parse+stringify of
  // a multi-MB array. 10 tiles competing for shared localStorage every 30s
  // = visible freezes. Async-defer everything; the run object reference
  // is captured in the closure so it stays stable.
  setTimeout(() => {
    try {
      const raw = localStorage.getItem(LOCAL_RUNS_KEY);
      const arr: unknown[] = raw ? JSON.parse(raw) : [];
      arr.push({ t: new Date().toISOString(), title, run });
      const trimmed = arr.length > LOCAL_RUNS_MAX_ENTRIES ? arr.slice(-LOCAL_RUNS_MAX_ENTRIES) : arr;
      localStorage.setItem(LOCAL_RUNS_KEY, JSON.stringify(trimmed));
    } catch { /* quota / private mode — drop */ }
    void sendBotRunToSupabase(run, title).catch(() => {
      // localStorage backup already attempted — silently swallow
    });
  }, 0);
}

// BETA 15.28 — Fields stripped from the run object before Supabase send.
// Per Dylan: descriptions were exceeding the 100KB cap and getting truncated
// mid-string, breaking `description::jsonb` cast in SQL queries. These six
// JSON-stringified streams are forensic-detail fields (per-frame samples,
// every-event logs, heatmap data) — useful for deep debugging from the local
// CSV but NOT needed for balance/dashboards. Supabase keeps the slim run
// (~5-15KB instead of 50-300KB), localStorage backup keeps the full run.
const BOT_RUN_HEAVY_FIELDS = [
  'botPositionTrailJson',     // 5s-interval position samples × 60+ minutes = huge
  'stuckEventsJson',          // every stuck event with full nearby-enemies snapshot
  'botMapCellVisitsJson',     // map heatmap dictionary (can be 1000+ keys)
  'decisionTraceJson',        // per-pick decision trace from PSBOT instrumentation
  'eventLogJson',             // every kill / pickup / shrine / level-up event
  'floorEffectTrackerJson',   // verbose ground-effect spawn/trigger log
];

// BETA 15.91 — Live-play telemetry. Per Dylan: "we need normal live plays to
// submit every time" + "with the exact same data we get from bot runs. and the
// user who was playing." When this fires from a normal (non-botfarm) tab, we
// switch the category to 'live_play' (so PSBOT RUN can filter live vs bot
// cleanly with `WHERE category = 'live_play'`) and stuff the player's username
// into the reporter field. Username comes from sessionTracker which is set on
// startSession at game enter. Falls back to 'anonymous' if no name is set.
function getLivePlayUsername(): string {
  // BETA 16.06 — Was reading localStorage('goopzone_goop_system').username
  // but the goop save doesn't actually contain a username field. Verified
  // via Supabase: 16.04 row tagged 'live-play:anonymous' even though Dylan
  // was signed in as DevPeanut. Now reads from sessionTracker which gets
  // the real username via startSession() on game-mount.
  try {
    const u = getSessionData()?.username;
    if (u && typeof u === 'string' && u.length > 0) return u;
  } catch { /* fall through */ }
  return 'anonymous';
}

// BETA 20.67 — SAFE-FIT serialization (tracking deep-dive CRITICAL #1). The
// 15.28 strip list stopped covering the payload after 16.21/19.93/20.45 grew
// it (humanTrace / samples10s / pickEvents+statsAtPick): runs ≳5 min blew the
// 100KB cap and the raw .slice() cut MID-JSON → description::jsonb cast
// failed → the whole row was unusable (12% of recent live rows, all the
// longest/most valuable runs — a regression of the exact bug 15.28 fixed).
// Strategy: (a) diet the big arrays keeping the most-recent TAILS (the
// Charter's death-shape window is the last ~30s, so tails are the valuable
// part), then (b) if still over budget, drop the LARGEST remaining fields
// whole — recording them in `truncatedFields` so the analyst knows exactly
// what was sacrificed — re-stringifying each pass. NEVER slice a JSON string.
export function buildSafeDescription(run: object): string {
  const slim: Record<string, unknown> = { ...(run as Record<string, unknown>) };
  for (const field of BOT_RUN_HEAVY_FIELDS) delete slim[field];
  try {
    const ht = slim.humanTrace;
    if (Array.isArray(ht) && ht.length > 90) slim.humanTrace = ht.slice(-90); // ~last 30s @ 3Hz
    const s10 = slim.samples10s;
    if (Array.isArray(s10) && s10.length > 60) slim.samples10s = s10.slice(-60); // last 10 min
    const pe = slim.pickEvents;
    if (Array.isArray(pe) && pe.length > 20) {
      const keepFrom = pe.length - 20; // full statsAtPick only on the last 20 picks
      slim.pickEvents = pe.map((ev, i) => {
        if (i >= keepFrom || !ev || typeof ev !== 'object') return ev;
        const { statsAtPick: _omit, ...rest } = ev as Record<string, unknown>;
        return rest;
      });
    }
  } catch { /* diet is best-effort */ }
  try {
    const SAFE_BUDGET = 95_000; // headroom under the 100KB row cap (RUN_DESCRIPTION_MAX_BYTES)
    let json = JSON.stringify(slim);
    const dropped: string[] = [];
    while (json.length > SAFE_BUDGET) {
      let worstKey: string | null = null;
      let worstSize = 4_000; // never bother dropping small fields
      for (const [k, v] of Object.entries(slim)) {
        if (k === 'truncatedFields') continue;
        const sz = typeof v === 'string' ? v.length
          : (v && typeof v === 'object') ? JSON.stringify(v).length : 0;
        if (sz > worstSize) { worstKey = k; worstSize = sz; }
      }
      if (!worstKey) break; // only small scalars left — fits by construction
      dropped.push(worstKey);
      delete slim[worstKey];
      slim.truncatedFields = dropped;
      json = JSON.stringify(slim);
    }
    return json;
  } catch { return '{"error":"serialization-failed"}'; }
}

// BETA 20.67 — last-gasp flush (tracking deep-dive HIGH #2). Tab close /
// refresh / browser crash produced ZERO telemetry: no pagehide handler
// existed anywhere, and the normal insert isn't keepalive (a death followed
// by an immediate close lost the row too). navigator.sendBeacon survives
// unload; beacons can't set headers, so the Supabase apikey rides the query
// string (Supabase REST accepts it).
let _unloadProvider: (() => object | null) | null = null;
let _unloadInstalled = false;
export function registerUnloadRunProvider(fn: () => object | null): void {
  _unloadProvider = fn;
  if (_unloadInstalled || typeof window === 'undefined') return;
  _unloadInstalled = true;
  window.addEventListener('pagehide', () => {
    try {
      const run = _unloadProvider?.();
      if (!run) return;
      const url = import.meta.env.VITE_SUPABASE_URL as string | undefined;
      const key = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;
      if (!url || !key) return;
      const runRec = run as Record<string, unknown>;
      const isBot = runRec.botEnabled === true;
      const row = {
        reporter: isBot ? 'bot:unload' : `live-play:${getLivePlayUsername()}`,
        category: isBot ? 'bot_run' : 'live_play',
        title: 'RUN',
        description: buildSafeDescription(run),
        screen: 'play',
      };
      navigator.sendBeacon(
        `${url}/rest/v1/bug_reports?apikey=${encodeURIComponent(key)}`,
        new Blob([JSON.stringify(row)], { type: 'application/json' }),
      );
      // BETA 21.26 — also RANK force-closed runs on the leaderboard. Dylan: LogiCC
      // force-closed an immortal run and it never hit the board. The normal score
      // submit only fires on the death-screen QUIT path, which a hard close skips —
      // so a long survival run vanished. Beacon a leaderboard row too (live runs
      // only; bots never rank). Anon insert (user_id null + username) — the same
      // path tester-code players use; beacons can't carry an auth JWT. Score
      // formula MUST match submitScore() in supabase.ts.
      if (!isBot) {
        const _name = getLivePlayUsername();
        const _kills = Math.max(0, Math.round(Number(runRec.killCount ?? 0)));
        const _floor = Math.max(1, Math.round(Number(runRec.floorReached ?? 1)));
        const _time = Math.max(0, Number(runRec.survivalTimeSec ?? 0));
        const _char = String(runRec.characterId ?? 'unknown');
        if (_name && _time >= 30) {
          const _score = _floor * 5000000 + Math.floor(_time) * 100 + _kills * 5;
          const lbRow = {
            user_id: null,
            username: _name,
            kills: _kills,
            floor_reached: _floor,
            time_survived: _time,
            character_id: _char,
            score: _score,
          };
          navigator.sendBeacon(
            `${url}/rest/v1/leaderboard?apikey=${encodeURIComponent(key)}`,
            new Blob([JSON.stringify(lbRow)], { type: 'application/json' }),
          );
        }
      }
    } catch { /* best effort — never break unload */ }
  });
}

async function sendBotRunToSupabase(run: object, title: string): Promise<void> {
  try {
    const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
    const tile = params.get('tile') ?? 'main';
    const isBotFarm = params.get('botfarm') === 'true';
    // BETA 15.91 — Username in reporter for live play. Bot rows still use
    // `bot:<tile>` so existing bot CSV pipelines are unchanged.
    // BETA 20.67 — cohort integrity (deep-dive CRITICAL #7 / HIGH #3/#8):
    // category no longer trusts the URL param alone. autoPlaytester stamps
    // `botEnabled` into every payload from live bot state — Dylan's standard
    // local BOT RUN flow (normal tab, no ?botfarm=true) was landing in
    // live_play and contaminating human-balance queries. RUN_PARTIAL /
    // freeze snapshots get their own `*_partial` category so analyst
    // averages never ingest the mostly-zero 5-minute snapshots (the
    // confirmed "itemsCollected=0" mystery).
    const _runRec = run as Record<string, unknown>;
    const isBotRun = isBotFarm || _runRec.botEnabled === true;
    const reporter = isBotRun
      ? `bot:${tile}`
      : `live-play:${getLivePlayUsername()}`;
    let category = isBotRun ? 'bot_run' : 'live_play';
    if (title !== 'RUN') category = `${category}_partial`;

    const description = buildSafeDescription(run);

    await supabase.from('bug_reports').insert({
      reporter,
      category,
      title,
      description,
      screen: isBotRun ? 'botfarm' : 'play',
    });
  } catch { /* silently swallow */ }
}
